import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import * as authService from '../services/auth'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => authService.getCurrentUser())
  const [token, setToken] = useState(() => authService.getToken())
  const [authReady, setAuthReady] = useState(!authService.getToken())

  useEffect(() => {
    let cancelled = false

    async function restore() {
      const session = await authService.restoreSession()
      if (cancelled) return
      if (session?.user && session?.token) {
        setUser(session.user)
        setToken(session.token)
      } else {
        setUser(null)
        setToken('')
      }
      setAuthReady(true)
    }

    restore()
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      token,
      authReady,
      isAuthenticated: Boolean(user && token),
      async login(payload) {
        const result = await authService.login(payload)
        setUser(result.user)
        setToken(result.token)
        return result
      },
      async refreshUser() {
        const session = await authService.refreshCurrentUser()
        if (session?.user) setUser(session.user)
        return session?.user || null
      },
      logout() {
        authService.logout()
        setUser(null)
        setToken('')
      },
    }),
    [user, token, authReady],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth는 AuthProvider 안에서만 사용할 수 있습니다.')
  return ctx
}
